import { FieldProps, FormControl, useFormField } from '@core/utils/form';
import { TimeInput, toMoment } from '@core/utils/time';
import FormHelperText from '@mui/material/FormHelperText';

import moment from 'moment';
import React, { Fragment, useCallback, useMemo } from 'react';
import { FormGroup, Label, UncontrolledTooltip } from 'reactstrap';
import './form_date.scss';
import { R } from '@core/utils/r';
import { DatePicker } from 'antd';
import type { Moment } from 'moment';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import { DATE_FORMAT } from '@business/constants/time';
const MomentDatePicker = DatePicker.generatePicker<Moment>(momentGenerateConfig);

export interface FormDateInputProps extends FieldProps<FormControl<number> | FormControl<number | null>> {
  minDate?: TimeInput;
  maxDate?: TimeInput;
  onChange?(date: number | null): void;
  appendToBody?: boolean;
  anchorDirection?: 'left' | 'right';
  withoutGroup?: boolean;
  asterisk?: boolean;
  isDateBlocked?(date: moment.Moment): boolean;
  clearable?: boolean;
  description?: string;
  container?: string;
  endOfDay?: boolean;
}

export function FormDateInput(props: FormDateInputProps) {
  const { formControl, fieldState, fieldConfig } = useFormField(props);

  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;
  const fieldValue = fieldState.value ? toMoment(fieldState.value) : null;
  const fieldError = (fieldTouched && fieldState.errorMessage) || null;

  const fieldReadonly = fieldConfig.readonly || false;
  const fieldDisabled = fieldConfig.disabled || false;

  const fieldPlaceholder = fieldConfig.placeholder || 'mm/dd/yyyy';

  const handleChange = useCallback(
    (date: moment.Moment | null) => {
      if (date) {
        if (props.endOfDay) {
          formControl.setValue(date.endOf('day').unix());
          props.onChange && props.onChange(date.endOf('day').unix());
        } else {
          formControl.setValue(date.unix());
          props.onChange && props.onChange(date.unix());
        }
      } else {
        formControl.setValue(null);
        props.onChange && props.onChange(null);
      }
    },
    [formControl, props],
  );

  const handleFocus = useCallback(
    (isFocused: boolean) => {
      if (isFocused) {
        formControl.onFocus();
      } else {
        formControl.onBlur();
      }
    },
    [formControl],
  );

  const isDayBlocked = useCallback(
    (m: moment.Moment) => {
      let isBlocked = false;
      if (props.isDateBlocked) {
        isBlocked = props.isDateBlocked(m);
      }

      if (props.minDate) {
        return m.isBefore(toMoment(props.minDate).startOf('day').subtract(1, 'h')) || isBlocked;
      }
      if (props.maxDate) {
        return m.isAfter(toMoment(props.minDate).endOf('day').add(1, 'h')) || isBlocked;
      }

      return isBlocked;
    },
    [props],
  );

  const tooltip = useMemo(() => {
    if (!props.description) return null;
    const uid = formControl.uid.replace('form:', '');
    return (
      <Fragment>
        <i className="fas fa-question-circle ml-2" id={`control-description-${uid}`}></i>
        <UncontrolledTooltip container={props.container} placement="top" target={`control-description-${uid}`}>
          {props.description}
        </UncontrolledTooltip>
      </Fragment>
    );
  }, [formControl.uid, props.container, props.description]);

  const content = (
    <Fragment>
      {fieldConfig.label && (
        <Fragment>
          <Label for={formControl.uid}>
            {fieldConfig.label} {fieldConfig.required && props.asterisk && <span className="text-danger">*</span>}
          </Label>
          {fieldConfig.required && !R.isEmpty(fieldConfig.label) && (
            <span className="text-danger font-size-14" style={{ marginTop: -5, marginLeft: 2 }}>
              *
            </span>
          )}

          {tooltip}
        </Fragment>
      )}
      <MomentDatePicker
        size="middle"
        style={{ width: '100%' }}
        format={DATE_FORMAT}
        value={fieldValue}
        disabled={fieldDisabled}
        readOnly={fieldReadonly}
        id={formControl.uid}
        placeholder={fieldPlaceholder}
        allowClear={props.clearable}
        onChange={value => handleChange(value)}
        disabledDate={isDayBlocked}
      />
      {!!fieldError && (
        <FormHelperText className="mui-error" style={{ color: '#f44336' }}>
          {fieldError}
        </FormHelperText>
      )}
    </Fragment>
  );

  if (props.withoutGroup) {
    return (
      <div id={`form-date-input-${formControl.uid}`} className="theme_date-picker">
        {content}
      </div>
    );
  }

  return (
    <FormGroup id={`form-date-input-${formControl.uid}`} className="theme_date-picker">
      {content}
    </FormGroup>
  );
}
